/**
 * Filters for reports.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/forms/Button';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { SearchInput } from '@/components/forms/SearchInput';
import { Select } from '@/components/forms/Select';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { useUsersSearch } from '@/hooks/useUsersSearch';
import { cn } from '@/lib/utils';
import {
  ManagedByMultiSelect,
  type ManagedByValue,
} from '@/components/common/ManagedByMultiSelect';
import {
  REMOTE_REPORT_TUTORING_STATUS_CONFIG,
  REMOTE_REPORT_SYNTHESIS_STATUS_CONFIG,
  REMOTE_REPORT_SCORING_STATUS_CONFIG,
  REPORT_REMOTE_SEARCH_PLACEHOLDERS,
  DATE_RANGE_PERIOD_OPTIONS_FULL,
} from '@/components/reports/report-configs';
import {
  getEffectiveReportSessionTypes,
  getReportStatusKeys,
  isAllReportsTab,
  isTutoringReportTab,
  toRemoteReportProgramSelectOptions,
} from './report-table-utils';
import type { ReportTab as Tab } from '@/types/remote/report.types';
import type { ReportRemoteFilters as Filters } from '@/components/reports/report-configs';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { getSchoolYears } from '@/services/school-years.service';
import { listScoped as listStudentsRemoteScoped } from '@/services/remote/students.service';

/** Props for {@link ReportFilters}. */
interface ReportFiltersProps {
  activeTab: Tab;
  filters: Filters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onToggleStatus: (status: string) => void;
  onToggleScoring: (scoring: string) => void;
  onSetDateRange: (range: Filters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetSearch: (value: string | undefined) => void;
  onSetTutorFilter: (id: string | undefined) => void;
  onSetStudentFilter: (id: string | undefined) => void;
  onSetManagedByFilter: (value: ManagedByValue) => void;
  onSetProgramFilter: (id: string | undefined) => void;
  onSetSchoolYearFilter: (id: string | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Free-text search for remote report filters. */
const SearchFilter: React.FC<{
  filters: Filters;
  onSetSearch: (value: string | undefined) => void;
  activeTab: Tab;
}> = ({ filters, onSetSearch, activeTab }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-56">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder={REPORT_REMOTE_SEARCH_PLACEHOLDERS[activeTab]}
        value={filters.search ?? ''}
        onSearch={(q) => onSetSearch(q.trim() || undefined)}
        onClear={() => onSetSearch(undefined)}
      />
    </div>
  </div>
);

/** Preset period chips for remote report filters. */
const PeriodFilter: React.FC<{
  filters: Filters;
  onSetDateRange: (range: Filters['dateRange']) => void;
}> = ({ filters, onSetDateRange }) => (
  <div className="filter-group">
    <span className="filter-group-label">Période</span>
    <SegmentedControl
      size="sm"
      value={(filters.customDateFrom ? '__custom__' : filters.dateRange) ?? 'all'}
      onChange={(v) => onSetDateRange(v as Filters['dateRange'])}
      options={[...DATE_RANGE_PERIOD_OPTIONS_FULL]}
    />
  </div>
);

/** Custom from/to date range for remote report filters. */
const DateRangeFilter: React.FC<{
  filters: Filters;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetCustomDateRange }) => {
  const toDateString = (d?: Date) => {
    if (!d) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fromStr = toDateString(filters.customDateFrom);
  const toStr = toDateString(filters.customDateTo);

  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.customDateTo);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 0, 0, 0);
    onSetCustomDateRange(d, filters.customDateTo);
  };

  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.customDateFrom, undefined);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 23, 59, 59);
    onSetCustomDateRange(filters.customDateFrom, d);
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date personnalisée</span>
      <div className="w-auto mt-0.5">
        <DateRangeInput
          startDate={fromStr}
          endDate={toStr}
          onStartDateChange={handleFromChange}
          onEndDateChange={handleToChange}
          layout="horizontal"
          showConnector={true}
          showPresets={false}
          hidePickerLabels={true}
          compact={true}
        />
      </div>
    </div>
  );
};

/** One report-status chip used by the remote status filter. */
const StatusChip: React.FC<{
  statusKey: string;
  isActive: boolean;
  onToggle: (status: string) => void;
}> = ({ statusKey, isActive, onToggle }) => {
  const statusConfig = {
    ...REMOTE_REPORT_TUTORING_STATUS_CONFIG,
    ...REMOTE_REPORT_SYNTHESIS_STATUS_CONFIG,
  };
  const config = statusConfig[statusKey];
  if (!config) return null;
  const Icon = config.icon;
  return (
    <Button
      key={statusKey}
      variant="custom"
      size="sm"
      icon={Icon}
      iconPosition="left"
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
        isActive ? 'filter-badge-active' : 'filter-badge-inactive',
      )}
      style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
      onClick={() => onToggle(statusKey)}
    >
      {config.label}
    </Button>
  );
};

/** Report-status chips, optionally grouped by tutoring vs other sessions. */
const StatusFilter: React.FC<{
  filters: Filters;
  onToggleStatus: (status: string) => void;
  statusKeys: string[];
  groupByWorkflow?: boolean;
}> = ({ filters, onToggleStatus, statusKeys, groupByWorkflow = false }) => {
  const tutoringKeys = statusKeys.filter((k) => k in REMOTE_REPORT_TUTORING_STATUS_CONFIG);
  const synthesisKeys = statusKeys.filter((k) => k in REMOTE_REPORT_SYNTHESIS_STATUS_CONFIG);
  const showGroups = groupByWorkflow && tutoringKeys.length > 0 && synthesisKeys.length > 0;

  if (!showGroups) {
    return (
      <div className="filter-group">
        <span className="filter-group-label">Statut rapport</span>
        <div className="filter-options">
          {statusKeys.map((key) => (
            <StatusChip
              key={key}
              statusKey={key}
              isActive={filters.reportStatus.includes(key)}
              onToggle={onToggleStatus}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut CR</span>
      <div className="flex flex-col gap-2">
        <div className="filter-options">
          <span className="text-xs font-medium text-[var(--color-2-nuance-2)] self-center mr-1">
            Tutorat
          </span>
          {tutoringKeys.map((key) => (
            <StatusChip
              key={key}
              statusKey={key}
              isActive={filters.reportStatus.includes(key)}
              onToggle={onToggleStatus}
            />
          ))}
        </div>
        <div className="filter-options">
          <span className="text-xs font-medium text-[var(--color-2-nuance-2)] self-center mr-1">
            Autres séances
          </span>
          {synthesisKeys.map((key) => (
            <StatusChip
              key={key}
              statusKey={key}
              isActive={filters.reportStatus.includes(key)}
              onToggle={onToggleStatus}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/** Scoring-status chips for remote report filters. */
const ScoringFilter: React.FC<{
  filters: Filters;
  onToggleScoring: (scoring: string) => void;
}> = ({ filters, onToggleScoring }) => {
  const scoringKeys = Object.keys(REMOTE_REPORT_SCORING_STATUS_CONFIG);

  return (
    <div className="filter-group">
      <span className="filter-group-label">Statut Scoring</span>
      <div className="filter-options">
        {scoringKeys.map((key) => {
          const config = REMOTE_REPORT_SCORING_STATUS_CONFIG[key];
          if (!config) return null;
          const Icon = config.icon;
          const isActive = filters.scoringStatus.includes(key);
          return (
            <Button
              key={key}
              variant="custom"
              size="sm"
              icon={Icon}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
                isActive ? 'filter-badge-active' : 'filter-badge-inactive',
              )}
              style={{ '--filter-color': config.badgeColor } as React.CSSProperties}
              onClick={() => onToggleScoring(key)}
            >
              {config.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Remote tutor autocomplete for report filters. */
const TutorFilter: React.FC<{
  filters: Filters;
  onSetTutorFilter: (id: string | undefined) => void;
}> = ({ filters, onSetTutorFilter }) => {
  const {
    users: tutorUsers,
    isLoading,
    onSearch,
  } = useUsersSearch({
    role: 'tutor_remote',
    pageSize: 20,
  });
  const options = tutorUsers.map((u) => ({
    value: u.id,
    label: u.displayName || `${u.firstName} ${u.lastName}`,
  }));

  return (
    <div className="filter-group">
      <span className="filter-group-label">Tuteur</span>
      <div className="w-48">
        <Autocomplete
          inputSize="sm"
          placeholder="Rechercher un tuteur..."
          options={options}
          value={filters.tutorUserId ?? ''}
          onChange={(val) => onSetTutorFilter(val || undefined)}
          onSearch={onSearch}
          isLoading={isLoading}
          minSearchLength={2}
        />
      </div>
    </div>
  );
};

/** Remote student autocomplete for report filters. */
const StudentFilter: React.FC<{
  filters: Filters;
  onSetStudentFilter: (id: string | undefined) => void;
}> = ({ filters, onSetStudentFilter }) => {
  const [searchQuery, setSearchQuery] = React.useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['reports-remote', 'filter-students', searchQuery],
    queryFn: () =>
      listStudentsRemoteScoped({
        search: searchQuery || undefined,
        tabView: 'students',
        pageSize: 20,
      }),
  });
  const options = React.useMemo(
    () =>
      (data?.data ?? []).map((s) => ({
        value: s.userId,
        label:
          `${s.user?.firstName ?? ''} ${s.user?.lastName ?? ''}`.trim() ||
          s.user?.email ||
          s.userId,
      })),
    [data?.data],
  );

  return (
    <div className="filter-group">
      <span className="filter-group-label">Élève</span>
      <div className="w-48">
        <Autocomplete
          inputSize="sm"
          placeholder="Rechercher un élève..."
          options={options}
          value={filters.studentUserId ?? ''}
          onChange={(val) => onSetStudentFilter(val || undefined)}
          onSearch={setSearchQuery}
          isLoading={isLoading}
          minSearchLength={2}
        />
      </div>
    </div>
  );
};

/** Program select for remote report filters. */
const ProgramFilter: React.FC<{
  filters: Filters;
  onSetProgramFilter: (id: string | undefined) => void;
}> = ({ filters, onSetProgramFilter }) => {
  const { data: programsResponse } = useProgramsForMode();
  const selectOptions = React.useMemo(
    () => toRemoteReportProgramSelectOptions(programsResponse?.data ?? []),
    [programsResponse],
  );

  return (
    <div className="filter-group">
      <span className="filter-group-label">Programme</span>
      <div className="w-48">
        <Select
          key={filters.programId ?? '__empty__'}
          placeholder="Tous les programmes"
          options={selectOptions}
          value={filters.programId ?? ''}
          onChange={(val) => onSetProgramFilter(val || undefined)}
          size="sm"
        />
      </div>
    </div>
  );
};

/** School-year select for remote report filters. */
const SchoolYearFilter: React.FC<{
  filters: Filters;
  onSetSchoolYearFilter: (id: string | undefined) => void;
}> = ({ filters, onSetSchoolYearFilter }) => {
  const { data: schoolYearsData } = useQuery({
    queryKey: ['school-years', 'filter-reports-remote'],
    queryFn: () => getSchoolYears({ pageSize: 20 }),
  });
  const options = React.useMemo(
    () =>
      schoolYearsData?.data.map((sy) => ({
        value: sy.id,
        label: sy.label ?? sy.code ?? sy.id,
      })) ?? [],
    [schoolYearsData],
  );
  return (
    <div className="filter-group">
      <span className="filter-group-label">Année scolaire</span>
      <div className="w-48">
        <Select
          key={filters.schoolYearId ?? '__empty__'}
          placeholder="Toutes les années"
          size="sm"
          value={filters.schoolYearId ?? ''}
          onChange={(v) => onSetSchoolYearFilter(v || undefined)}
          options={options}
        />
      </div>
    </div>
  );
};

/** Filter fields for the remote reports list. */
export const ReportFilters: React.FC<ReportFiltersProps> = ({
  activeTab,
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onToggleStatus,
  onToggleScoring,
  onSetDateRange,
  onSetCustomDateRange,
  onSetSearch,
  onSetTutorFilter,
  onSetStudentFilter,
  onSetManagedByFilter,
  onSetProgramFilter,
  onSetSchoolYearFilter,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  const effectiveTypes = React.useMemo(
    () => getEffectiveReportSessionTypes(activeTab, filters.sessionTypes ?? []),
    [activeTab, filters.sessionTypes],
  );

  const statusKeys = React.useMemo(() => getReportStatusKeys(effectiveTypes), [effectiveTypes]);

  const showTutoringSpecific =
    isTutoringReportTab(activeTab) ||
    (isAllReportsTab(activeTab) && effectiveTypes.includes('individual_tutoring'));

  return (
    <div className="filter-groups-container">
      {show('filter-search') && (
        <SearchFilter filters={filters} onSetSearch={onSetSearch} activeTab={activeTab} />
      )}

      {show('filter-period') && <PeriodFilter filters={filters} onSetDateRange={onSetDateRange} />}

      {show('filter-date-range') && (
        <DateRangeFilter filters={filters} onSetCustomDateRange={onSetCustomDateRange} />
      )}

      {show('filter-status-report') && (
        <StatusFilter
          filters={filters}
          onToggleStatus={onToggleStatus}
          statusKeys={statusKeys}
          groupByWorkflow={isAllReportsTab(activeTab)}
        />
      )}

      {show('filter-status-scoring') && (
        <ScoringFilter filters={filters} onToggleScoring={onToggleScoring} />
      )}

      {showTutoringSpecific && show('filter-program') && (
        <ProgramFilter filters={filters} onSetProgramFilter={onSetProgramFilter} />
      )}

      {showTutoringSpecific && show('filter-tutor') && (
        <TutorFilter filters={filters} onSetTutorFilter={onSetTutorFilter} />
      )}

      {showTutoringSpecific && show('filter-student') && (
        <StudentFilter filters={filters} onSetStudentFilter={onSetStudentFilter} />
      )}

      {show('filter-managed-by') && (
        <div className="filter-group">
          <span className="filter-group-label">Géré par</span>
          <div className="w-64">
            <ManagedByMultiSelect
              value={filters.managedByIds}
              onChange={onSetManagedByFilter}
              inputSize="sm"
            />
          </div>
        </div>
      )}

      {show('filter-school-year') && (
        <SchoolYearFilter filters={filters} onSetSchoolYearFilter={onSetSchoolYearFilter} />
      )}
    </div>
  );
};
